import { useEffect, useCallback, useState, useRef } from "react";
import { io, Socket } from "socket.io-client";
import env from "../services/environment";
import { AudioStreamer } from "@/utils/audio-streamer";

interface MediaChunk {
  mimeType: string;
  data: string;
}

interface UseVoiceCoachProps {
  botId: string;
  audioStreamer: AudioStreamer | null;
}

const DEFAULT_ERROR =
  "We are currently experiencing high demand on this service! Please try again later.";

export function useVoiceCoach({ botId, audioStreamer }: UseVoiceCoachProps) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSetupComplete, setIsSetupComplete] = useState(false);
  const [response, setResponse] = useState<ArrayBuffer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const setupTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const connect = useCallback(() => {
    if (isLoading || isSetupComplete) {
      return;
    }
    if (socket?.connected) {
      socket.disconnect();
      setSocket(null);
    }

    setIsLoading(true);
    setIsSetupComplete(false);

    const clientId = `client_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    const newSocket = io(env().SERVER_API_URL + "/genai", {
      auth: {
        clientId,
      },
      transports: ["websocket"],
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 3,
      reconnectionDelay: 1000,
    });

    setSocket(newSocket);
    newSocket.connect();
  }, [isLoading, isSetupComplete, socket]);

  useEffect(() => {
    if (!socket) return;

    const handleConnect = () => {
      console.log("Establishing connection to GenAI...");
      if (!botId) {
        setError("Bot ID not provided");
        return;
      }
      socket.emit("genai-setup", {
        botId,
        status: "dev",
        allowReconnect: false,
      }); // defaults to "live" for embedding

      setupTimeoutRef.current = setTimeout(() => {
        setIsLoading(false);
        setError(DEFAULT_ERROR);
        socket.disconnect();
      }, 30000);

      setError(null);
    };

    const handleDisconnect = () => {
      setIsLoading(false);
      setIsSetupComplete(false);
    };

    const handleSetupComplete = () => {
      clearTimeout(setupTimeoutRef.current!);
      console.log("Setup:Complete, GenAI now ready to use!");
      setIsSetupComplete(true);
      setIsLoading(false);
    };

    const handleError = (error: {
      message: string;
      code: string;
      data?: any;
    }) => {
      console.error("voice-coach error:", error);
      clearTimeout(setupTimeoutRef.current!);
      socket.disconnect();
      setIsLoading(false);
      setIsSetupComplete(false);
      setError(error.message);
    };

    const handleAudio = (audioData: string) => {
      const data = base64ToArrayBuffer(audioData);
      setResponse(data);
    };

    const handleInterrupted = () => {
      audioStreamer?.stop();
    };

    socket.on("connect", () => {
      console.log("Connected to server");
    });
    socket.on("connection-complete", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("genai-setup-complete", handleSetupComplete);
    socket.on("error", handleError);
    socket.on("genai-audio", handleAudio);
    socket.on("genai-interrupted", handleInterrupted);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("genai-setup-complete", handleSetupComplete);
      socket.off("error", handleError);
      socket.off("genai-audio", handleAudio);
      socket.off("genai-interrupted", handleInterrupted);
      socket.disconnect();
    };
  }, [socket, botId]);

  const sendMediaChunk = useCallback(
    (mediaChunk: MediaChunk) => {
      if (!isSetupComplete && isLoading) return;
      if (!socket || !isSetupComplete) {
        setIsSetupComplete(false);
        return;
      }

      try {
        socket.emit("genai-real-time-input", {
          realtimeInput: {
            mediaChunks: [mediaChunk],
          },
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to send media chunk";
        console.error("Error sending media chunk:", errorMessage);
        setError(errorMessage);
      }
    },
    [socket, isSetupComplete],
  );

  return {
    connect,
    isLoading,
    isAvailable: isSetupComplete,
    response,
    error,
    sendMediaChunk,
  };
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}
